import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Save,
  Plus,
  Trash2,
  Image as ImageIcon,
  Layers,
  FileText,
  Package,
  Ruler,
  Globe,
  UploadCloud,
} from "lucide-react";
import * as adminApi from "../../api/adminApi";
import { resolveImageUrl } from "../../utils/images";
import { formatMoney, formatProductSize } from "../../utils/format";
import { getProductDetailUrl } from "../../utils/productSlug";
import { useToast } from "../../context/ToastContext";
import ColorPicker from "../../components/ColorPicker";
import "./AdminProducts.css";
import "./ProductEdit.css";

const SIZE_CATEGORIES = [
  {
    category: "Adult Standard",
    items: [
      { value: "XXS", label: "XXS" },
      { value: "XS", label: "XS" },
      { value: "S", label: "S" },
      { value: "M", label: "M" },
      { value: "L", label: "L" },
      { value: "XL", label: "XL" },
    ],
  },
  {
    category: "Plus Sizes",
    items: [
      { value: "XXL", label: "2XL (XXL)" },
      { value: "X3L", label: "3XL" },
      { value: "X4L", label: "4XL" },
      { value: "X5L", label: "5XL" },
      { value: "X6L", label: "6XL" },
      { value: "X7L", label: "7XL" },
    ],
  },
  {
    category: "Tall Variants",
    items: [
      { value: "LT", label: "Large Tall (LT)" },
      { value: "XLT", label: "XL Tall (XLT)" },
      { value: "XXLT", label: "2XL Tall (XXLT)" },
    ],
  },
  {
    category: "Toddlers & Kids",
    items: [
      { value: "T2", label: "2 Years (T2)" },
      { value: "T3", label: "3 Years (T3)" },
      { value: "T4", label: "4 Years (T4)" },
      { value: "T5", label: "5 Years (T5)" },
      { value: "Years6", label: "6 Years" },
      { value: "Years8", label: "8 Years" },
      { value: "Years10", label: "10 Years" },
      { value: "Years12", label: "12 Years" },
      { value: "Years14", label: "14 Years" },
    ],
  },
  {
    category: "Babies (Age-Based)",
    items: [
      { value: "Preemie", label: "Preemie" },
      { value: "Newborn", label: "Newborn" },
      { value: "Months0To3", label: "0 – 3 Months" },
      { value: "Months3To6", label: "3 – 6 Months" },
      { value: "Months6To9", label: "6 – 9 Months" },
      { value: "Months9To12", label: "9 – 12 Months" },
      { value: "Months12To18", label: "12 – 18 Months" },
      { value: "Months18To24", label: "18 – 24 Months" },
    ],
  },
];

function Modal({ children, onClose, title }) {
  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <div className="admin-modal">
        <h3>{title}</h3>
        <div className="admin-modal__content">{children}</div>
      </div>
    </div>
  );
}

function Confirm({ open, title, message, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <div className="admin-modal">
        <h3>{title}</h3>
        <p style={{ fontSize: "14px", color: "var(--text-secondary)", lineHeight: "1.5" }}>
          {message}
        </p>
        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" className="btn-outline" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("general");
  const [savingBasic, setSavingBasic] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Images state
  const [images, setImages] = useState([]);
  const [imageOrders, setImageOrders] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImageIds, setDeletingImageIds] = useState({});
  const [savingOrders, setSavingOrders] = useState(false);
  const [settingThumbnail, setSettingThumbnail] = useState(false);

  // Variants state
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [editingWeights, setEditingWeights] = useState({});
  const [savingWeightId, setSavingWeightId] = useState(null);

  // Modals state
  const [sizeModal, setSizeModal] = useState(false);
  const [colorModal, setColorModal] = useState(false);
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  // Forms
  const [sizeForm, setSizeForm] = useState({ size: "M", weight: 500 });
  const [colorForm, setColorForm] = useState({ color: "", colorHex: "#000000" });

  // Drag & drop
  const dragItem = useRef();
  const dragOverItem = useRef();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await adminApi.fetchAdminProduct(id);
        if (cancelled) return;
        setProduct({
          ...data,
          code: data.code || "",
          name: data.name || "",
          description: data.description || "",
          arabicName: data.arabicName || "",
          arabicDescription: data.arabicDescription || "",
          length: data.length ?? 0,
          width: data.width ?? 0,
          height: data.height ?? 0,
          basePrice: data.basePrice ?? 0,
          isEnabled: !!data.isEnabled,
        });
        const sortedImages = (data.productImages || [])
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setImages(sortedImages);
        const orders = {};
        sortedImages.forEach((img) => (orders[img.id] = img.displayOrder));
        setImageOrders(orders);
        setSizes(data.sizeVariants || []);
        setColors(data.colorVariants || []);

        const initialWeights = {};
        (data.sizeVariants || []).forEach((s) => {
          initialWeights[s.id] = s.weight;
        });
        setEditingWeights(initialWeights);
        setIsDirty(false);
      } catch (e) {
        addToast(e.message || "Failed to load product.", "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, addToast]);

  // ── Basic Info & Specs Update ──
  const handleFieldChange = (field, value) => {
    setProduct((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const saveBasic = async (e) => {
    if (e) e.preventDefault();
    if (!product.name.trim()) {
      addToast("Product name is required.", "error");
      return;
    }
    if (!product.code.trim()) {
      addToast("Product code (SKU) is required.", "error");
      return;
    }

    setSavingBasic(true);
    try {
      const payload = {
        code: product.code.trim(),
        name: product.name.trim(),
        description: product.description ? product.description.trim() : "",
        arabicName: product.arabicName ? product.arabicName.trim() : "",
        arabicDescription: product.arabicDescription ? product.arabicDescription.trim() : "",
        length: product.length !== "" ? Number(product.length) : 0,
        width: product.width !== "" ? Number(product.width) : 0,
        height: product.height !== "" ? Number(product.height) : 0,
        basePrice: product.basePrice !== "" ? Number(product.basePrice) : 0,
        isEnabled: !!product.isEnabled,
      };

      const updated = await adminApi.updateProduct(product.id, payload);
      setProduct((prev) => ({
        ...prev,
        ...updated,
        code: updated.code || payload.code,
        name: updated.name || payload.name,
        description: updated.description || payload.description,
        arabicName: updated.arabicName || payload.arabicName,
        arabicDescription: updated.arabicDescription || payload.arabicDescription,
        length: updated.length ?? payload.length,
        width: updated.width ?? payload.width,
        height: updated.height ?? payload.height,
        basePrice: updated.basePrice ?? payload.basePrice,
        isEnabled: updated.isEnabled ?? payload.isEnabled,
      }));
      setIsDirty(false);
      addToast("Product details saved successfully.", "success");
    } catch (err) {
      addToast(err.message || "Save failed.", "error");
    } finally {
      setSavingBasic(false);
    }
  };

  // ── Images Actions ──
  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await adminApi.addProductImage(id, file);
      setImages((s) => [...s, res]);
      setImageOrders((o) => ({ ...o, [res.id]: res.displayOrder }));
      if (!product.thumbnailImage) {
        setProduct((p) => ({ ...p, thumbnailImage: res.id }));
      }
      addToast("Image uploaded successfully.", "success");
    } catch (err) {
      addToast(err.message || "Upload failed.", "error");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const confirmDeleteImage = (imageId) => {
    setConfirmState({
      open: true,
      title: "Delete image",
      message: "Are you sure you want to delete this image? This action cannot be undone.",
      onConfirm: () => handleDeleteImage(imageId),
    });
  };

  const handleDeleteImage = async (imageId) => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    setDeletingImageIds((s) => ({ ...s, [imageId]: true }));
    try {
      await adminApi.deleteProductImage(id, imageId);
      setImages((s) => s.filter((i) => i.id !== imageId));
      const copy = { ...imageOrders };
      delete copy[imageId];
      setImageOrders(copy);
      if (product.thumbnailImage === imageId) {
        const remaining = images.filter((i) => i.id !== imageId);
        setProduct((p) => ({
          ...p,
          thumbnailImage: remaining.length > 0 ? remaining[0].id : null,
        }));
      }
      addToast("Image deleted.", "success");
    } catch (err) {
      addToast(err.message || "Delete failed.", "error");
    } finally {
      setDeletingImageIds((s) => {
        const copy = { ...s };
        delete copy[imageId];
        return copy;
      });
    }
  };

  const saveImagesOrder = async () => {
    setSavingOrders(true);
    const list = images.map((img, idx) => ({ id: img.id, displayOrder: idx }));
    try {
      const updated = await adminApi.updateImagesOrder(id, list);
      const sortedImages = (updated || [])
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setImages(sortedImages);
      const orders = {};
      sortedImages.forEach((img) => (orders[img.id] = img.displayOrder));
      setImageOrders(orders);
      addToast("Images reordered.", "success");
    } catch (err) {
      addToast(err.message || "Save order failed.", "error");
    } finally {
      setSavingOrders(false);
    }
  };

  const setAsThumbnail = async (imageId) => {
    setSettingThumbnail(true);
    try {
      const payload = { thumbnailImage: imageId };
      const updated = await adminApi.updateProduct(id, payload);
      setProduct((p) => ({ ...p, thumbnailImage: updated.thumbnailImage || imageId }));
      addToast("Thumbnail updated.", "success");
    } catch (err) {
      addToast(err.message || "Failed to set thumbnail.", "error");
    } finally {
      setSettingThumbnail(false);
    }
  };

  const handleDragStart = (e, position) => {
    dragItem.current = position;
  };
  const handleDragEnter = (e, position) => {
    dragOverItem.current = position;
  };
  const handleDrop = () => {
    const list = [...images];
    const dragIndex = dragItem.current;
    const hoverIndex = dragOverItem.current;
    if (dragIndex === undefined || hoverIndex === undefined) return;
    const draggedItem = list.splice(dragIndex, 1)[0];
    list.splice(hoverIndex, 0, draggedItem);
    dragItem.current = null;
    dragOverItem.current = null;
    setImages(list);
  };

  // ── Size Variants Actions ──
  const openAddSize = () => {
    // Pick first size not already added
    const existingSizes = new Set(sizes.map((s) => s.size));
    let defaultChoice = "M";
    for (const group of SIZE_CATEGORIES) {
      const found = group.items.find((item) => !existingSizes.has(item.value));
      if (found) {
        defaultChoice = found.value;
        break;
      }
    }
    setSizeForm({ size: defaultChoice, weight: 500 });
    setSizeModal(true);
  };

  const addSize = async () => {
    if (!sizeForm.size) {
      addToast("Please select a size.", "error");
      return;
    }
    try {
      const res = await adminApi.addSizeVariant(id, {
        size: sizeForm.size,
        weight: Number(sizeForm.weight) || 0,
      });
      setSizes((s) => [...s, res]);
      setEditingWeights((prev) => ({ ...prev, [res.id]: res.weight }));
      setSizeModal(false);
      addToast(`Size ${res.size} added.`, "success");
    } catch (err) {
      addToast(err.message || "Add size failed.", "error");
    }
  };

  const toggleSize = async (size) => {
    try {
      const res = await adminApi.updateSizeVariant(id, size.id, {
        isEnabled: !size.isEnabled,
        weight: size.weight,
      });
      setSizes((s) => s.map((x) => (x.id === res.id ? res : x)));
      addToast(`Size ${size.size} ${res.isEnabled ? "enabled" : "disabled"}.`, "success");
    } catch (err) {
      addToast(err.message || "Update size failed.", "error");
    }
  };

  const handleWeightSave = async (size) => {
    const newWeight = Number(editingWeights[size.id]);
    if (isNaN(newWeight) || newWeight < 0) {
      addToast("Please enter a valid weight in grams.", "error");
      return;
    }
    setSavingWeightId(size.id);
    try {
      const res = await adminApi.updateSizeVariant(id, size.id, {
        weight: newWeight,
        isEnabled: size.isEnabled,
      });
      setSizes((s) => s.map((x) => (x.id === res.id ? res : x)));
      addToast(`Weight for ${size.size} updated.`, "success");
    } catch (err) {
      addToast(err.message || "Failed to update weight.", "error");
    } finally {
      setSavingWeightId(null);
    }
  };

  const confirmDeleteSize = (size) => {
    setConfirmState({
      open: true,
      title: "Delete size variant",
      message: `Are you sure you want to delete size "${size.size}"? This cannot be undone.`,
      onConfirm: () => deleteSize(size),
    });
  };

  const deleteSize = async (size) => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    try {
      await adminApi.deleteSizeVariant(id, size.id);
      setSizes((s) => s.filter((x) => x.id !== size.id));
      addToast("Size deleted.", "success");
    } catch (err) {
      addToast(err.message || "Delete size failed.", "error");
    }
  };

  // ── Color Variants Actions ──
  const openAddColor = () => {
    setColorForm({ color: "", colorHex: "#2A2A2A" });
    setColorModal(true);
  };

  const addColor = async () => {
    if (!colorForm.color.trim()) {
      addToast("Please enter a color name.", "error");
      return;
    }
    const cleanHex = colorForm.colorHex.replace("#", "").trim();
    if (!/^[a-fA-F0-9]{3}$|^[a-fA-F0-9]{6}$/.test(cleanHex)) {
      addToast("Please enter a valid hex color code.", "error");
      return;
    }

    try {
      const res = await adminApi.addColorVariant(id, {
        color: colorForm.color.trim(),
        colorHex: cleanHex,
      });
      setColors((c) => [...c, res]);
      setColorModal(false);
      addToast(`Color "${res.color}" added. Upload an image to enable it.`, "success");
    } catch (err) {
      addToast(err.message || "Add color failed.", "error");
    }
  };

  const toggleColor = async (color) => {
    if (!color.isEnabled && !color.url) {
      addToast(`Cannot enable "${color.color}" without an image. Please upload a variant image first.`, "error");
      return;
    }
    try {
      const cleanHex = (color.colorHex || "000000").replace("#", "").trim();
      const res = await adminApi.updateColorVariant(id, color.id, {
        isEnabled: !color.isEnabled,
        colorHex: cleanHex,
      });
      setColors((s) => s.map((x) => (x.id === res.id ? res : x)));
      addToast(`Color "${color.color}" ${res.isEnabled ? "enabled" : "disabled"}.`, "success");
    } catch (err) {
      addToast(err.message || "Update color failed.", "error");
    }
  };

  const handleColorHexSave = async (colorId, newHex) => {
    const c = colors.find((x) => x.id === colorId);
    if (!c) return;
    const cleanHex = (newHex || "").replace("#", "").trim();
    if (!/^[a-fA-F0-9]{3}$|^[a-fA-F0-9]{6}$/.test(cleanHex)) {
      addToast("Invalid hex code format.", "error");
      return;
    }
    try {
      const payload = { colorHex: cleanHex, isEnabled: c.isEnabled };
      const updated = await adminApi.updateColorVariant(id, colorId, payload);
      setColors((s) => s.map((x) => (x.id === colorId ? updated : x)));
      addToast("Color updated.", "success");
    } catch (err) {
      addToast(err.message || "Failed to update color hex.", "error");
    }
  };

  const uploadColorImage = async (colorId, file, inputElement) => {
    if (!file) return;
    try {
      const res = await adminApi.addColorVariantImage(id, colorId, file);
      if (res.url) {
        res.url = `${res.url}?t=${new Date().getTime()}`;
      }
      setColors((s) => s.map((x) => (x.id === res.id ? res : x)));
      addToast("Color image uploaded successfully.", "success");
    } catch (err) {
      addToast(err.message || "Upload failed.", "error");
    } finally {
      if (inputElement) inputElement.value = null;
    }
  };

  const confirmDeleteColor = (c) => {
    setConfirmState({
      open: true,
      title: "Delete color variant",
      message: `Are you sure you want to delete color "${c.color}"? This cannot be undone.`,
      onConfirm: () => deleteColor(c),
    });
  };

  const deleteColor = async (c) => {
    setConfirmState((prev) => ({ ...prev, open: false }));
    try {
      await adminApi.deleteColorVariant(id, c.id);
      setColors((s) => s.filter((x) => x.id !== c.id));
      addToast("Color deleted.", "success");
    } catch (err) {
      addToast(err.message || "Delete color failed.", "error");
    }
  };

  // ── Calculations & Publishing Readiness ──
  const volume =
    (Number(product?.length) || 0) *
    (Number(product?.width) || 0) *
    (Number(product?.height) || 0);

  const getBostaTier = (vol) => {
    if (vol <= 0) return { name: "Not Specified", desc: "Enter length, width, and height to calculate package size." };
    if (vol < 5280) return { name: "Medium Package", desc: "Standard parcel (< 5,280 cm³)." };
    if (vol < 9240) return { name: "Large Package", desc: "Large parcel (5,280 – 9,239 cm³)." };
    if (vol <= 14175) return { name: "Extra Large Package", desc: "Bulk parcel (9,240 – 14,175 cm³)." };
    return { name: "Exceeds Standard Limit", desc: "Volume > 14,175 cm³. Exceeds automated delivery limits." };
  };

  const bostaTier = getBostaTier(volume);

  const hasImages = images.length > 0;
  const hasEnabledSizes = sizes.some((s) => s.isEnabled);
  const hasEnabledColorsWithImage = colors.some((c) => c.isEnabled && c.url);
  const hasValidDimensions =
    (Number(product?.length) || 0) > 0 &&
    (Number(product?.width) || 0) > 0 &&
    (Number(product?.height) || 0) > 0;

  const isReadyToPublish =
    hasImages && hasEnabledSizes && hasEnabledColorsWithImage && hasValidDimensions;

  if (loading) {
    return (
      <div className="page-loading">
        <span className="page-loading__dot" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pe-container" style={{ textAlign: "center", paddingTop: 80 }}>
        <p className="form-error">Product could not be found.</p>
        <button type="button" className="btn-primary" onClick={() => navigate("/admin/products")}>
          Back to Products
        </button>
      </div>
    );
  }

  return (
    <div className="pe-container">
      {/* ── Page Header ── */}
      <header className="pe-header">
        <div>
          <nav className="pe-header__breadcrumb" aria-label="Breadcrumb">
            <Link to="/admin/products">Products</Link>
            <span>/</span>
            <span>Edit</span>
          </nav>
          <div className="pe-header__title-row">
            <h1 className="pe-header__title">{product.name || "Untitled Product"}</h1>
            <span className="pe-badge pe-badge--code">{product.code || "NO-SKU"}</span>
            <span className={`pe-badge ${product.isEnabled ? "pe-badge--live" : "pe-badge--draft"}`}>
              {product.isEnabled ? "● Live on Store" : "○ Draft"}
            </span>
          </div>
        </div>

        <div className="pe-header__actions">
          <button
            type="button"
            className="btn-outline"
            onClick={() => navigate("/admin/products")}
            title="Back to products list"
          >
            <ArrowLeft size={15} /> Back
          </button>
          {product.isEnabled && (
            <Link
              to={getProductDetailUrl(product)}
              state={{ productId: product.id }}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              title="Open product on storefront"
            >
              <ExternalLink size={15} /> View Store
            </Link>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={saveBasic}
            disabled={savingBasic}
          >
            <Save size={15} />
            {savingBasic ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </header>

      {/* ── Navigation Tabs ── */}
      <nav className="pe-tabs" role="tablist" aria-label="Product Sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "general"}
          className={`pe-tab-btn ${activeTab === "general" ? "pe-tab-btn--active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          <FileText size={16} />
          Details & Localization
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "media"}
          className={`pe-tab-btn ${activeTab === "media" ? "pe-tab-btn--active" : ""}`}
          onClick={() => setActiveTab("media")}
        >
          <ImageIcon size={16} />
          Media & Gallery
          <span className="pe-tab-count">{images.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "variants"}
          className={`pe-tab-btn ${activeTab === "variants" ? "pe-tab-btn--active" : ""}`}
          onClick={() => setActiveTab("variants")}
        >
          <Layers size={16} />
          Variants (Sizes & Colors)
          <span className="pe-tab-count">{sizes.length + colors.length}</span>
        </button>
      </nav>

      {/* ── Tab 1: General Details, Arabic Content & Dimensions ── */}
      {activeTab === "general" && (
        <div className="pe-grid">
          <div>
            {/* English Information */}
            <section className="pe-card">
              <div className="pe-card__head">
                <div>
                  <h2 className="pe-card__title">
                    <FileText size={18} /> General Information
                  </h2>
                  <p className="pe-card__subtitle">Primary English product identification and pricing.</p>
                </div>
              </div>

              <div className="pe-field-group">
                <div className="pe-field">
                  <label htmlFor="prod-code">
                    Product Code (SKU) <span className="pe-field-hint">Unique identifier</span>
                  </label>
                  <input
                    id="prod-code"
                    type="text"
                    className="pe-input"
                    value={product.code}
                    onChange={(e) => handleFieldChange("code", e.target.value)}
                    placeholder="e.g., TS-COTTON-001"
                    required
                  />
                </div>

                <div className="pe-field">
                  <label htmlFor="prod-base-price">
                    Base Price (EGP) <span className="pe-field-hint">{formatMoney(product.basePrice || 0)}</span>
                  </label>
                  <input
                    id="prod-base-price"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pe-input"
                    value={product.basePrice}
                    onChange={(e) => handleFieldChange("basePrice", e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div className="pe-field">
                <label htmlFor="prod-name">Product Name (English)</label>
                <input
                  id="prod-name"
                  type="text"
                  className="pe-input"
                  value={product.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  placeholder="e.g., Premium Heavyweight Cotton T-Shirt"
                  required
                />
              </div>

              <div className="pe-field" style={{ marginBottom: 0 }}>
                <label htmlFor="prod-desc">Product Description (English)</label>
                <textarea
                  id="prod-desc"
                  rows={4}
                  maxLength={1000}
                  className="pe-textarea"
                  value={product.description}
                  onChange={(e) => handleFieldChange("description", e.target.value)}
                  placeholder="Describe the product material, fit, features, and styling..."
                />
                <div className="pe-char-count">{(product.description || "").length}/1000</div>
              </div>
            </section>

            {/* Arabic Localization */}
            <section className="pe-card">
              <div className="pe-card__head">
                <div>
                  <h2 className="pe-card__title">
                    <Globe size={18} /> Arabic Localization (المحتوى العربي)
                  </h2>
                  <p className="pe-card__subtitle">
                    Arabic translation displayed to Arabic-speaking shoppers on the storefront.
                  </p>
                </div>
              </div>

              <div className="pe-field">
                <label htmlFor="prod-name-ar">
                  اسم المنتج بالعربية (Arabic Name)
                  <span className="pe-field-hint">RTL text</span>
                </label>
                <input
                  id="prod-name-ar"
                  type="text"
                  dir="rtl"
                  lang="ar"
                  className="pe-input pe-input--rtl"
                  value={product.arabicName || ""}
                  onChange={(e) => handleFieldChange("arabicName", e.target.value)}
                  placeholder="مثال: تيشيرت قطن بريميوم بوزن ثقيل"
                />
              </div>

              <div className="pe-field" style={{ marginBottom: 0 }}>
                <label htmlFor="prod-desc-ar">
                  وصف المنتج بالعربية (Arabic Description)
                  <span className="pe-field-hint">RTL text</span>
                </label>
                <textarea
                  id="prod-desc-ar"
                  dir="rtl"
                  lang="ar"
                  rows={4}
                  maxLength={1000}
                  className="pe-textarea pe-textarea--rtl"
                  value={product.arabicDescription || ""}
                  onChange={(e) => handleFieldChange("arabicDescription", e.target.value)}
                  placeholder="اكتب مواصفات المنتج وخامته ومميزاته باللغة العربية..."
                />
                <div className="pe-char-count">{(product.arabicDescription || "").length}/1000</div>
              </div>
            </section>

            {/* Physical Dimensions & Shipping Specs */}
            <section className="pe-card">
              <div className="pe-card__head">
                <div>
                  <h2 className="pe-card__title">
                    <Ruler size={18} /> Physical Dimensions & Shipping Specs
                  </h2>
                  <p className="pe-card__subtitle">
                    Required for Bosta courier shipping calculation and package categorization.
                  </p>
                </div>
              </div>

              <div className="pe-dimensions-grid">
                <div className="pe-dimension-box">
                  <label htmlFor="dim-length">Length (الطول)</label>
                  <div className="pe-dimension-input-wrap">
                    <input
                      id="dim-length"
                      type="number"
                      step="0.1"
                      min="0"
                      value={product.length ?? ""}
                      onChange={(e) => handleFieldChange("length", e.target.value)}
                      placeholder="0.0"
                    />
                    <span className="pe-unit">cm</span>
                  </div>
                </div>

                <div className="pe-dimension-box">
                  <label htmlFor="dim-width">Width (العرض)</label>
                  <div className="pe-dimension-input-wrap">
                    <input
                      id="dim-width"
                      type="number"
                      step="0.1"
                      min="0"
                      value={product.width ?? ""}
                      onChange={(e) => handleFieldChange("width", e.target.value)}
                      placeholder="0.0"
                    />
                    <span className="pe-unit">cm</span>
                  </div>
                </div>

                <div className="pe-dimension-box">
                  <label htmlFor="dim-height">Height (الارتفاع)</label>
                  <div className="pe-dimension-input-wrap">
                    <input
                      id="dim-height"
                      type="number"
                      step="0.1"
                      min="0"
                      value={product.height ?? ""}
                      onChange={(e) => handleFieldChange("height", e.target.value)}
                      placeholder="0.0"
                    />
                    <span className="pe-unit">cm</span>
                  </div>
                </div>
              </div>

              {/* Bosta Courier Volumetric Calculation Card */}
              <div className="pe-shipping-calc-card">
                <Package size={22} style={{ color: "var(--gold-dark)", flexShrink: 0, marginTop: 2 }} />
                <div className="pe-shipping-calc-card__content">
                  <div className="pe-shipping-calc-card__title">
                    Volumetric Size: {volume > 0 ? `${volume.toLocaleString()} cm³` : "0 cm³"}
                  </div>
                  <div className="pe-shipping-calc-card__tier">
                    Bosta Shipping Category: {bostaTier.name}
                  </div>
                  <p className="pe-shipping-calc-card__desc">{bostaTier.desc}</p>
                </div>
              </div>
            </section>
          </div>

          {/* Right Sidebar: Status & Publishing Readiness Checklist */}
          <aside>
            <div className="pe-readiness-card">
              <div className="pe-readiness-card__header">
                <h3 className="pe-readiness-card__title">Publishing Status</h3>
                <span className={`pe-badge ${product.isEnabled ? "pe-badge--live" : "pe-badge--draft"}`}>
                  {product.isEnabled ? "Live" : "Draft"}
                </span>
              </div>

              {/* Enable / Disable Switch */}
              <div className="pe-switch-wrap">
                <div className="pe-switch-label">
                  <strong>Enable Product</strong>
                  <span>{product.isEnabled ? "Visible on storefront" : "Hidden from shoppers"}</span>
                </div>
                <label className="pe-switch">
                  <input
                    type="checkbox"
                    checked={product.isEnabled || false}
                    onChange={(e) => {
                      const willEnable = e.target.checked;
                      if (willEnable && !isReadyToPublish) {
                        addToast(
                          "Cannot enable product until all prerequisites (images, dimensions, enabled size, enabled color with image) are satisfied.",
                          "warning"
                        );
                      }
                      handleFieldChange("isEnabled", willEnable);
                    }}
                  />
                  <span className="pe-slider" />
                </label>
              </div>

              {/* Checklist */}
              <h4 style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 12 }}>
                Publishing Readiness Checklist
              </h4>
              <ul className="pe-checklist">
                <li className="pe-checklist__item">
                  <div className={`pe-checklist__icon ${hasImages ? "pe-checklist__icon--valid" : "pe-checklist__icon--invalid"}`}>
                    {hasImages ? <Check size={12} /> : "!"}
                  </div>
                  <div className="pe-checklist__text">
                    <strong>Product Images</strong>
                    <span>{hasImages ? `${images.length} image(s) uploaded` : "Requires at least 1 image"}</span>
                  </div>
                </li>

                <li className="pe-checklist__item">
                  <div className={`pe-checklist__icon ${hasValidDimensions ? "pe-checklist__icon--valid" : "pe-checklist__icon--invalid"}`}>
                    {hasValidDimensions ? <Check size={12} /> : "!"}
                  </div>
                  <div className="pe-checklist__text">
                    <strong>Physical Dimensions</strong>
                    <span>
                      {hasValidDimensions
                        ? `${product.length} × ${product.width} × ${product.height} cm`
                        : "Length, width, and height must be > 0"}
                    </span>
                  </div>
                </li>

                <li className="pe-checklist__item">
                  <div className={`pe-checklist__icon ${hasEnabledSizes ? "pe-checklist__icon--valid" : "pe-checklist__icon--invalid"}`}>
                    {hasEnabledSizes ? <Check size={12} /> : "!"}
                  </div>
                  <div className="pe-checklist__text">
                    <strong>Size Variants</strong>
                    <span>
                      {hasEnabledSizes
                        ? `${sizes.filter((s) => s.isEnabled).length} enabled size(s)`
                        : "Requires at least 1 enabled size"}
                    </span>
                  </div>
                </li>

                <li className="pe-checklist__item">
                  <div className={`pe-checklist__icon ${hasEnabledColorsWithImage ? "pe-checklist__icon--valid" : "pe-checklist__icon--invalid"}`}>
                    {hasEnabledColorsWithImage ? <Check size={12} /> : "!"}
                  </div>
                  <div className="pe-checklist__text">
                    <strong>Color Variants</strong>
                    <span>
                      {hasEnabledColorsWithImage
                        ? `${colors.filter((c) => c.isEnabled && c.url).length} ready color(s)`
                        : "Requires at least 1 enabled color with an image"}
                    </span>
                  </div>
                </li>
              </ul>

              <button
                type="button"
                className="btn-primary"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={saveBasic}
                disabled={savingBasic}
              >
                <Save size={15} />
                {savingBasic ? "Saving Changes…" : "Save All Details"}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ── Tab 2: Media & Gallery ── */}
      {activeTab === "media" && (
        <section className="pe-card">
          <div className="pe-card__head">
            <div>
              <h2 className="pe-card__title">
                <ImageIcon size={18} /> Product Images Gallery
              </h2>
              <p className="pe-card__subtitle">
                Drag to reorder images, assign main storefront thumbnail, and manage product photography.
              </p>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <label
                className="btn-primary"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}
              >
                <UploadCloud size={16} />
                {uploadingImage ? "Uploading…" : "Upload Image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddImage}
                  style={{ display: "none" }}
                  disabled={uploadingImage}
                />
              </label>

              {images.length > 1 && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={saveImagesOrder}
                  disabled={savingOrders}
                >
                  {savingOrders ? "Saving Order…" : "Save Image Order"}
                </button>
              )}
            </div>
          </div>

          {images.length === 0 ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                border: "2px dashed var(--border)",
                borderRadius: "var(--radius)",
                background: "var(--surface-input)",
              }}
            >
              <ImageIcon size={40} style={{ color: "var(--text-muted)", marginBottom: 12 }} />
              <p style={{ fontWeight: 500, color: "var(--text-primary)", marginBottom: 4 }}>
                No product images uploaded yet
              </p>
              <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: 16 }}>
                Upload at least one high-resolution image to showcase this product and enable it on the storefront.
              </p>
              <label className="btn-primary" style={{ cursor: "pointer", display: "inline-flex" }}>
                <UploadCloud size={16} /> Select Photo to Upload
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAddImage}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          ) : (
            <ul className="pe-gallery-grid">
              {images.map((img, index) => {
                const isThumb = product.thumbnailImage === img.id;
                return (
                  <li
                    key={img.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    className={`pe-gallery-item ${isThumb ? "pe-gallery-item--thumbnail" : ""}`}
                    onDragEnd={() => {
                      dragItem.current = null;
                      dragOverItem.current = null;
                    }}
                  >
                    <div className="pe-gallery-preview">
                      <img src={resolveImageUrl(img.url)} alt={`Product photography ${index + 1}`} />
                      {isThumb && <div className="pe-gallery-thumb-tag">Main Thumbnail</div>}
                    </div>

                    <div className="pe-gallery-footer">
                      <span className="pe-gallery-order">#{index + 1}</span>
                      <div className="pe-gallery-actions">
                        <button
                          type="button"
                          className="btn-ghost"
                          title="Set as main store thumbnail"
                          style={{ padding: "4px 8px", fontSize: "11px" }}
                          onClick={() => setAsThumbnail(img.id)}
                          disabled={settingThumbnail || isThumb}
                        >
                          {isThumb ? "✓ Primary" : "Set Primary"}
                        </button>
                        <button
                          type="button"
                          className="btn-ghost"
                          title="Delete image"
                          style={{ padding: "4px 8px", fontSize: "11px", color: "#8b4545" }}
                          onClick={() => confirmDeleteImage(img.id)}
                          disabled={deletingImageIds[img.id]}
                        >
                          {deletingImageIds[img.id] ? "…" : <Trash2 size={13} />}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ── Tab 3: Variants (Sizes & Colors) ── */}
      {activeTab === "variants" && (
        <div>
          {/* Size Variants Table */}
          <section className="pe-card pe-table-card">
            <div className="pe-card__head">
              <div>
                <h2 className="pe-card__title">
                  <Layers size={18} /> Size Variants ({sizes.length})
                </h2>
                <p className="pe-card__subtitle">
                  Manage sizes and parcel weight for shipping calculations.
                </p>
              </div>

              <button type="button" className="btn-primary" onClick={openAddSize}>
                <Plus size={15} /> Add Size
              </button>
            </div>

            {sizes.length === 0 ? (
              <div
                style={{
                  padding: "36px 20px",
                  textAlign: "center",
                  border: "1px dashed var(--border)",
                  borderRadius: "var(--radius)",
                }}
              >
                <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>
                  No sizes added yet. Click <strong>+ Add Size</strong> to create one.
                </p>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Size Code</th>
                      <th>Display Label</th>
                      <th>Weight (grams)</th>
                      <th style={{ width: "120px" }}>Status</th>
                      <th style={{ width: "100px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <strong>{s.size}</strong>
                        </td>
                        <td>
                          <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
                            {formatProductSize(s.size)}
                          </span>
                        </td>
                        <td>
                          <div className="pe-weight-input-wrap">
                            <input
                              type="number"
                              min="0"
                              className="pe-weight-input"
                              value={editingWeights[s.id] ?? s.weight}
                              onChange={(e) =>
                                setEditingWeights((prev) => ({
                                  ...prev,
                                  [s.id]: e.target.value,
                                }))
                              }
                              onBlur={() => {
                                if (Number(editingWeights[s.id]) !== s.weight) {
                                  handleWeightSave(s);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleWeightSave(s);
                              }}
                            />
                            <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>g</span>
                            {Number(editingWeights[s.id]) !== s.weight && (
                              <button
                                type="button"
                                className="btn-ghost"
                                style={{ padding: "2px 6px", fontSize: "11px" }}
                                onClick={() => handleWeightSave(s)}
                                disabled={savingWeightId === s.id}
                              >
                                {savingWeightId === s.id ? "…" : "Save"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <label
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              cursor: "pointer",
                              marginBottom: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={s.isEnabled}
                              onChange={() => toggleSize(s)}
                              style={{ cursor: "pointer", width: "auto" }}
                            />
                            <span
                              style={{
                                fontSize: "12px",
                                color: s.isEnabled ? "var(--text-primary)" : "var(--text-muted)",
                                fontWeight: s.isEnabled ? 500 : "normal",
                              }}
                            >
                              {s.isEnabled ? "Enabled" : "Disabled"}
                            </span>
                          </label>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="btn-ghost"
                            style={{ fontSize: "12px", color: "#8b4545", padding: "4px 8px" }}
                            onClick={() => confirmDeleteSize(s)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Color Variants Table */}
          <section className="pe-card pe-table-card">
            <div className="pe-card__head">
              <div>
                <h2 className="pe-card__title">
                  <PaletteIcon size={18} /> Color Variants ({colors.length})
                </h2>
                <p className="pe-card__subtitle">
                  Configure color swatches and variant-specific photography.
                </p>
              </div>

              <button type="button" className="btn-primary" onClick={openAddColor}>
                <Plus size={15} /> Add Color
              </button>
            </div>

            {colors.length === 0 ? (
              <div
                style={{
                  padding: "36px 20px",
                  textAlign: "center",
                  border: "1px dashed var(--border)",
                  borderRadius: "var(--radius)",
                }}
              >
                <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>
                  No colors added yet. Click <strong>+ Add Color</strong> to create one.
                </p>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Color Name</th>
                      <th style={{ width: "160px" }}>Hex Code & Swatch</th>
                      <th style={{ width: "120px" }}>Variant Image</th>
                      <th style={{ width: "120px" }}>Status</th>
                      <th style={{ width: "100px", textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colors.map((c) => {
                      const cleanHex = (c.colorHex || "000000").replace("#", "");
                      return (
                        <tr key={c.id}>
                          <td>
                            <strong>{c.color}</strong>
                          </td>
                          <td>
                            <ColorPicker
                              initialHex={cleanHex}
                              onSave={(newHex) => handleColorHexSave(c.id, newHex)}
                            />
                          </td>
                          <td>
                            {c.url ? (
                              <div className="color-variant-image-wrap">
                                <img
                                  src={resolveImageUrl(c.url)}
                                  alt={`Variant ${c.color}`}
                                  style={{
                                    width: 80,
                                    height: 60,
                                    objectFit: "cover",
                                    borderRadius: "4px",
                                    display: "block",
                                  }}
                                />
                                <label className="color-variant-image-overlay">
                                  <span>Replace</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                      uploadColorImage(c.id, e.target.files?.[0], e.target)
                                    }
                                    style={{ display: "none" }}
                                  />
                                </label>
                              </div>
                            ) : (
                              <label className="color-variant-no-image" title="Click to upload variant image">
                                <span
                                  style={{
                                    fontSize: "10px",
                                    color: "var(--text-muted)",
                                    textAlign: "center",
                                    lineHeight: "1.2",
                                  }}
                                >
                                  + Photo
                                  <br />
                                  (Required)
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) =>
                                    uploadColorImage(c.id, e.target.files?.[0], e.target)
                                  }
                                  style={{ display: "none" }}
                                />
                              </label>
                            )}
                          </td>
                          <td>
                            <label
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "pointer",
                                marginBottom: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={c.isEnabled}
                                onChange={() => toggleColor(c)}
                                style={{ cursor: "pointer", width: "auto" }}
                              />
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: c.isEnabled ? "var(--text-primary)" : "var(--text-muted)",
                                  fontWeight: c.isEnabled ? 500 : "normal",
                                }}
                              >
                                {c.isEnabled ? "Enabled" : "Disabled"}
                              </span>
                            </label>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button
                              type="button"
                              className="btn-ghost"
                              style={{ fontSize: "12px", color: "#8b4545", padding: "4px 8px" }}
                              onClick={() => confirmDeleteColor(c)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Unsaved Floating Prompt ── */}
      {isDirty && (
        <aside className="pe-floating-save" role="status">
          <span>You have unsaved product changes</span>
          <button
            type="button"
            className="btn-primary"
            style={{ padding: "8px 18px", fontSize: "12px" }}
            onClick={saveBasic}
            disabled={savingBasic}
          >
            {savingBasic ? "Saving…" : "Save Now"}
          </button>
        </aside>
      )}

      {/* ── Add Size Modal ── */}
      {sizeModal && (
        <Modal onClose={() => setSizeModal(false)} title="Add Size Variant">
          <div className="pe-field">
            <label htmlFor="modal-size-select">Select Size</label>
            <select
              id="modal-size-select"
              className="pe-select"
              value={sizeForm.size}
              onChange={(e) => setSizeForm({ ...sizeForm, size: e.target.value })}
            >
              {SIZE_CATEGORIES.map((cat) => {
                const existingSizes = new Set(sizes.map((s) => s.size));
                return (
                  <optgroup key={cat.category} label={cat.category}>
                    {cat.items.map((item) => {
                      const alreadyAdded = existingSizes.has(item.value);
                      return (
                        <option key={item.value} value={item.value} disabled={alreadyAdded}>
                          {item.label} {alreadyAdded ? "(Already Added)" : ""}
                        </option>
                      );
                    })}
                  </optgroup>
                );
              })}
            </select>
          </div>

          <div className="pe-field">
            <label htmlFor="modal-size-weight">
              Weight in Grams <span className="pe-field-hint">e.g., 500g for a t-shirt</span>
            </label>
            <input
              id="modal-size-weight"
              type="number"
              min="0"
              className="pe-input"
              value={sizeForm.weight}
              onChange={(e) =>
                setSizeForm({ ...sizeForm, weight: Number(e.target.value) || 0 })
              }
              placeholder="e.g., 500"
            />
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn-outline" onClick={() => setSizeModal(false)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={addSize}>
              Add Size
            </button>
          </div>
        </Modal>
      )}

      {/* ── Add Color Modal ── */}
      {colorModal && (
        <Modal onClose={() => setColorModal(false)} title="Add Color Variant">
          <div className="pe-field">
            <label htmlFor="modal-color-name">Color Name (English)</label>
            <input
              id="modal-color-name"
              type="text"
              className="pe-input"
              value={colorForm.color}
              onChange={(e) => setColorForm({ ...colorForm, color: e.target.value })}
              placeholder="e.g., Navy Blue, Charcoal, Olive Green"
              required
            />
          </div>

          <div className="pe-field">
            <label htmlFor="modal-color-hex">Color Swatch (Hex)</label>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="color"
                value={
                  colorForm.colorHex.startsWith("#")
                    ? colorForm.colorHex
                    : `#${colorForm.colorHex}`
                }
                onChange={(e) => setColorForm({ ...colorForm, colorHex: e.target.value })}
                style={{
                  width: 52,
                  height: 40,
                  cursor: "pointer",
                  border: "1px solid var(--border)",
                  borderRadius: "4px",
                  padding: 2,
                }}
              />
              <div style={{ flex: 1 }}>
                <input
                  id="modal-color-hex"
                  type="text"
                  className="pe-input"
                  value={colorForm.colorHex}
                  onChange={(e) => {
                    const val = e.target.value;
                    setColorForm({ ...colorForm, colorHex: val });
                  }}
                  placeholder="#000000"
                  style={{ fontFamily: "monospace" }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="btn-outline" onClick={() => setColorModal(false)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={addColor}>
              Add Color
            </button>
          </div>
        </Modal>
      )}

      {/* ── Confirmation Dialog ── */}
      <Confirm
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onCancel={() => setConfirmState((prev) => ({ ...prev, open: false }))}
        onConfirm={() => {
          if (confirmState.onConfirm) confirmState.onConfirm();
        }}
      />
    </div>
  );
}

// Simple internal icon helper for color palette
function PaletteIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
      <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
      <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
      <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}
